import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Button, Icon, SectionCard } from "@/deps/ui-library";
import { useApproveBill, useBill, useBillCredits } from "@/api/queries";
import {
  ApprovalActions,
  ApprovalCard,
  useApprovalHoldsDraft,
} from "@/components/DocumentApproval";
import { useCan } from "@/app/session";
import { DocumentScreen, LinesTable } from "@/components/DocumentScreen";
import { DraftActions } from "@/components/DraftActions";
import { UseCreditDialog } from "./UseCreditDialog";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { termsLabel } from "@/lib/paymentTerms";
import { useTaxWords } from "@/app/session";

export function BillDetailPage() {
  const say = useTaxWords();
  const { id } = useParams({ from: "/purchases/bills/$id" });
  const { data, isPending, error } = useBill(id);
  const approve = useApproveBill();
  const held = useApprovalHoldsDraft("bill", id, true);
  const can = useCan();
  const navigate = useNavigate();
  const [usingCredit, setUsingCredit] = useState(false);
  const { data: credits } = useBillCredits(id, data?.status === "approved");

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading bill…</p>;
  if (error || !data)
    return <p className="text-bad-text p-6 text-sm">That bill could not be loaded.</p>;

  const blocked = Number(data.nonRecoverableTax) > 0;

  return (
    <>
      {usingCredit ? (
        <UseCreditDialog
          bill={data}
          credits={credits?.available ?? []}
          onClose={() => setUsingCredit(false)}
        />
      ) : null}
      <DocumentScreen
        title="Bill"
        number={data.number}
        status={data.status}
        locked={data.status === "approved"}
        subtitle={`${data.supplierName} · ${formatDate(data.date)}`}
        actions={
          data.status !== "approved" ? (
            <span className="flex items-center gap-2">
              {approve.isError ? (
                <span role="alert" className="text-bad-text text-xs">
                  {approve.error instanceof Error
                    ? approve.error.message
                    : "That could not be approved."}
                </span>
              ) : null}
              {!held && can("purchases.bills.create") ? (
                <DraftActions
                  kind="bills"
                  label="bill"
                  id={id}
                  onEdit={() => void navigate({ to: "/purchases/bills/$id/edit", params: { id } })}
                  onDeleted={() => void navigate({ to: "/purchases/bills" })}
                />
              ) : null}
              <ApprovalActions
                kind="bill"
                id={id}
                canCreate={can("purchases.bills.create")}
                canIssue={can("purchases.bills.approve")}
                issuing={approve.isPending}
                onIssue={() => approve.mutate(id)}
                issueLabel="Approve and post"
                approveLabel="Approve and post"
                workingLabel="Approving"
              />
            </span>
          ) : data.status === "approved" && Number(data.balance) > 0 ? (
            <span className="flex items-center gap-2">
              {can("purchases.payments.create") && (credits?.available.length ?? 0) > 0 ? (
                <Button
                  colorScheme="default"
                  variant="outline"
                  size="sm"
                  onClick={() => setUsingCredit(true)}
                >
                  Use credit
                </Button>
              ) : null}
              {can("purchases.debitnotes.create") ? (
                <Button
                  colorScheme="default"
                  variant="outline"
                  size="sm"
                  leftIcon="FilePlus"
                  onClick={() =>
                    void navigate({ to: "/purchases/debit-notes/new", search: { bill: id } })
                  }
                >
                  Debit note
                </Button>
              ) : null}
              {can("purchases.payments.create") ? (
                <Button
                  colorScheme="primary"
                  variant="solid"
                  size="sm"
                  leftIcon="ArrowUpRight"
                  onClick={() =>
                    void navigate({ to: "/purchases/payments/new", search: { bill: id } })
                  }
                >
                  Record payment
                </Button>
              ) : null}
            </span>
          ) : undefined
        }
        facts={[
          { label: "Supplier", value: data.supplierName },
          ...(data.fromPurchaseOrder
            ? [
                {
                  label: "From purchase order",
                  value: (
                    <Link
                      to="/purchases/orders/$id"
                      params={{ id: data.fromPurchaseOrder.id }}
                      className="num text-primary underline"
                    >
                      {data.fromPurchaseOrder.number}
                    </Link>
                  ),
                },
              ]
            : []),
          {
            label: "Their invoice",
            value: <span className="num text-xs">{data.vendorInvoiceNo}</span>,
          },
          { label: "Date", value: formatDate(data.date) },
          { label: "Due", value: formatDate(data.due) },
          ...(data.paymentTermsDays === undefined
            ? []
            : [{ label: "Payment terms", value: termsLabel(data.paymentTermsDays) }]),
          { label: "Balance", value: <span className="num">{formatAmount(data.balance)}</span> },
        ]}
        history={data.history}
        aside={
          <>
            <ApprovalCard kind="bill" id={id} />
            {(credits?.used.length ?? 0) > 0 ? (
              <SectionCard title="Credits used">
                <ul className="flex flex-col gap-1.5">
                  {credits!.used.map((credit, index) => (
                    <li
                      key={`${credit.sourceId}-${index}`}
                      className="flex items-baseline justify-between gap-2 text-xs"
                    >
                      <span className="num text-fg">{credit.number}</span>
                      <span className="text-fg-muted">{formatDate(credit.date)}</span>
                      <span className="num text-fg">{formatAmount(credit.amount)}</span>
                    </li>
                  ))}
                </ul>
              </SectionCard>
            ) : data.attachments.length > 0 ? (
              <SectionCard title="Attachments">
                <ul className="flex flex-col gap-1.5">
                  {data.attachments.map((attachment) => (
                    <li key={attachment.id} className="flex items-center gap-2 text-xs">
                      <Icon name="Paperclip" size="sm" />
                      <span className="text-fg flex-1 truncate">{attachment.name}</span>
                      <span className="num text-fg-hint">
                        {Math.round(attachment.sizeBytes / 1024)} KB
                      </span>
                    </li>
                  ))}
                </ul>
              </SectionCard>
            ) : null}
          </>
        }
      >
        <SectionCard title="Lines" flush>
          <LinesTable
            caption={`Lines of bill ${data.number ?? "draft"}`}
            lines={data.lines.map((line) => ({
              n: line.n,
              label: line.description,
              account: `${line.accountName}${line.capital ? " · capital" : ""}`,
              quantity: line.quantity,
              unitPrice: formatAmount(line.unitPrice),
              tax: line.taxLabel,
              net: formatAmount(line.lineNet),
            }))}
          />
        </SectionCard>

        <SectionCard title="Tax">
          <dl className="flex flex-col gap-2 text-sm">
            <div className="flex items-baseline justify-between">
              <dt className="text-fg-muted">Subtotal</dt>
              <dd className="num text-fg">{formatAmount(data.subtotal)}</dd>
            </div>
            <div className="flex items-baseline justify-between">
              <dt className="text-fg-muted">{say("Recoverable VAT")}</dt>
              <dd className="num text-fg">{formatAmount(data.recoverableVat)}</dd>
            </div>
            <div className="flex items-baseline justify-between">
              <dt className="text-fg-muted">{say("Blocked VAT")}</dt>
              <dd className="num text-fg">{formatAmount(data.nonRecoverableTax)}</dd>
            </div>
            <div className="border-line-soft flex items-baseline justify-between border-t pt-2 font-semibold">
              <dt className="text-fg">Total</dt>
              <dd className="num text-fg">{formatAmount(data.total)}</dd>
            </div>
          </dl>

          {/* The explainer the design asks for. Recoverable and blocked tax post differently, and
            somebody choosing a tax code needs to know which one they are choosing. */}
          <p className="text-fg-muted mt-3 text-xs">
            {blocked
              ? "This bill carries blocked input tax. It is capitalised into the expense rather than claimed back, so the whole amount reaches the profit and loss."
              : say(
                  "Input tax on this bill is recoverable. It is posted separately from the expense and offsets the VAT you owe on sales.",
                )}
          </p>
        </SectionCard>
      </DocumentScreen>
    </>
  );
}
